<script lang="ts">
	/**
	 * `/Settings/Admin` — whether the store can take money, what is stopping it if it cannot, and
	 * how big the deployment behind it is.
	 *
	 * This is **not** a first-run wizard. It reads the same `setup-status` endpoint the checkout
	 * gate consults, and readiness is evaluated continuously (Design §4.5, §20): an admin can
	 * deactivate the default provider or disable the last currency at any time, and this page has
	 * to be as truthful on day four hundred as on day one. So it stays useful forever and shows a
	 * green state once the store is open, rather than disappearing after a first run.
	 *
	 * It absorbed the separate Store setup page, which is why it is the overview. Administration
	 * used to open on a page of user counts and shortcut tiles while the one question an operator
	 * actually arrives with — "is this store selling?" — sat a click away behind a second entry in
	 * the same sidebar. An overview that does not lead with it is an overview of the wrong thing.
	 *
	 * **Two loads, guarded separately.** Readiness and the counts fail for unrelated reasons, and
	 * either one hiding the other is how "the store is not open" goes unread because a count query
	 * timed out.
	 */

	import { onMount } from 'svelte';
	import { Badge, Button } from 'flowbite-svelte';
	import { EmptyState, PageHeader, Panel, RelativeDate, RoutedButton } from '../../components';
	import { push } from '../../svelte-spa-router';
	import { CurrencyFigure } from '../../components';
	import StatTile from './StatTile.svelte';
	import { getMetrics, getSetupStatus, listAdminCustomers, listAuditLogs, listUsers } from '../../AppNetwork';
	import { configurationStore } from '../../AppConfiguration';
	import type { AuditLog, Readiness, StoreMetrics, UnmetCondition } from '../../AppTypes';

	let readiness: Readiness | null = null;
	let loading: boolean = true;
	let loadError: string = '';

	/**
	 * The administration figures, which came across from the overview page with the rest of it.
	 *
	 * The two totals are exact — they are the `total` the API reports, not a count of what came
	 * back. The two derived ones are counted from the page of rows that came with it, so on a
	 * store with more users or more audit rows than one page they describe that page rather than
	 * the store; the API can express neither exactly (there is no `isAdmin` filter and no date
	 * filter on the audit list), and the audit log itself is the screen that answers both properly.
	 */
	let stats = {
		totalUsers: 0,
		adminUsers: 0,
		recentLogins: 0,
		auditEntries: 0
	};
	let countsLoading: boolean = true;
	let countsError: string = '';

	/**
	 * What the store is doing, as opposed to who has an account on it.
	 *
	 * A **third** guarded load, beside readiness and the account counts, for the reason those two
	 * are already separate: the three fail for unrelated reasons, and an operator must still be
	 * able to read "is this store selling" when the metrics query has timed out — or read the
	 * takings when `setup-status` has. It is also the slowest of the three, being aggregates over
	 * invoices and subscriptions, and holding the page for it would delay the one question this
	 * page exists to answer.
	 */
	let metrics: StoreMetrics | null = null;
	let metricsLoading: boolean = true;
	let metricsError: string = '';

	/** A fraction from the API — 0.05 is five per cent — and never rendered as one. */
	function fnPercent(rate: number): string {
		return `${(rate * 100).toFixed(1)}%`;
	}

	/**
	 * The conditions this build knows how to explain, in the order an operator should work
	 * through them. The server remains the authority on which are unmet; anything it reports that
	 * is not listed here still renders, generically, rather than being swallowed.
	 */
	const CHECKLIST: { condition: string; title: string; detail: string; href: string | null; action: string }[] = [
		{
			condition: 'settings.required',
			title: 'Required settings',
			detail:
				'Every setting marked required must resolve to a value. store.publicURL has no usable default on purpose, because a localhost default would open the store and mint localhost links into every receipt.',
			href: '/Settings/Admin/Settings',
			action: 'Open settings'
		},
		{
			condition: 'currency.enabled',
			title: 'An enabled currency',
			detail: 'At least one currency must be enabled for anything to have a price.',
			href: '/Settings/Admin/Settings/Currencies',
			action: 'Open currencies'
		},
		{
			condition: 'currency.default',
			title: 'A default currency',
			detail: 'One enabled currency must be the default new purchases are priced in.',
			href: '/Settings/Admin/Settings/Currencies',
			action: 'Open currencies'
		},
		{
			condition: 'provider.default',
			title: 'An active default payment provider',
			detail: 'One provider must be both active and default. Every new checkout is created against it.',
			href: '/Settings/Admin/Settings/Providers',
			action: 'Open payment providers'
		},
		{
			condition: 'provider.health',
			title: 'Verified provider credentials',
			detail:
				'The default provider must have verified its credentials. Verifying now is what keeps a typo from surfacing at a customer’s checkout instead.',
			href: '/Settings/Admin/Settings/Providers',
			action: 'Open payment providers'
		},
		{
			condition: 'provider.credentials',
			title: 'Readable provider credentials',
			detail:
				'The stored credentials must decrypt under the current encryption keyring. If they do not, a key version is missing from the environment.',
			href: '/Settings/Admin/Settings/Providers',
			action: 'Open payment providers'
		},
		{
			condition: 'tax.homeRate',
			title: 'A tax rate for your own country',
			detail:
				'Where the payment provider does not calculate tax, the store works it out from its own table, and refuses a sale it cannot price rather than charging nothing. Without an active rate for the country the store sells from, that is every domestic sale.',
			href: '/Settings/Admin/Settings/Tax',
			action: 'Open tax rates'
		},
		{
			condition: 'catalog.activeProduct',
			title: 'Something to sell',
			detail: 'At least one active product with an active price must exist.',
			href: null,
			action: ''
		}
	];

	$: unmetByCondition = new Map<string, UnmetCondition>(
		(readiness?.unmet ?? []).map(condition => [condition.condition, condition])
	);

	/** Anything the server reports that this build has no copy for. Rendered rather than dropped. */
	$: unrecognised = (readiness?.unmet ?? []).filter(
		unmet => !CHECKLIST.some(entry => entry.condition === unmet.condition)
	);

	async function fnLoad(): Promise<void> {
		loading = true;
		loadError = '';
		try {
			readiness = await getSetupStatus();
		} catch (error) {
			loadError = (error as Error).message;
		} finally {
			loading = false;
		}
	}

	async function fnLoadMetrics(): Promise<void> {
		metricsLoading = true;
		metricsError = '';
		try {
			metrics = await getMetrics();
		} catch (error) {
			metricsError = (error as Error).message;
		} finally {
			metricsLoading = false;
		}
	}

	async function fnLoadCounts(): Promise<void> {
		countsLoading = true;
		countsError = '';
		try {
			/*
			 * The administrator count is now **exact**, asked of the server with `isAdmin` and read
			 * off `total`. It used to be counted from whichever page of users happened to come
			 * back, so a store with more than a page of accounts reported the administrators *on
			 * that page* — a number that is wrong without looking wrong, on the figure that says
			 * how many people can change anything. `limit: 1` because only the total is wanted.
			 */
			const [users, audit, admins] = await Promise.all([
				listUsers(),
				listAuditLogs(),
				listAdminCustomers({ isAdmin: true, limit: 1 })
			]);

			stats.totalUsers = users.total;
			stats.adminUsers = admins.total;
			stats.auditEntries = audit.total;

			const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
			stats.recentLogins = audit.items.filter(
				(entry: AuditLog) => entry.action === 'USER_LOGIN' && new Date(entry.creationDate) > oneDayAgo
			).length;
		} catch (error) {
			countsError = (error as Error).message;
		} finally {
			countsLoading = false;
		}
	}

	/** Re-check reads all three, because all three are what the page is claiming. */
	async function fnRecheck(): Promise<void> {
		await Promise.all([fnLoad(), fnLoadCounts(), fnLoadMetrics()]);
	}

	function navigateToUsers() {
		push('/Settings/Admin/Customers');
	}

	function navigateToLogs() {
		push('/Settings/Admin/Logs');
	}

	onMount(() => {
		void fnLoad();
		void fnLoadCounts();
		void fnLoadMetrics();
	});
</script>

<PageHeader title="Administration">
	<svelte:fragment slot="description">
		Whether the store can take money right now, and what is standing in the way if it cannot.
	</svelte:fragment>
	<svelte:fragment slot="help">
		These conditions are re-checked on every checkout, not once at first run. If one stops being
		met (a provider deactivated, the last currency disabled) the storefront says “not open yet”
		rather than showing a Buy button that fails.
	</svelte:fragment>
</PageHeader>

<!--
	The banner of §11, and the reason it is a banner rather than a metric: once the store has taken
	a payment with no email provider configured, somebody has been charged and never receipted.
	That is a consumer-protection problem, not a polish item, so it stays in front of the operator
	until a provider is saved — and it is deliberately **not** a readiness condition, because
	refusing to sell over it would be a worse failure than the one it prevents.
-->
{#if readiness?.health?.email.unreceiptedSales}
	<div
		class="widget bg-amber-50 dark:bg-amber-950/40 border border-amber-300 dark:border-amber-700 mb-4 flex flex-wrap items-start gap-3"
		data-testid="unreceipted-sales-banner"
	>
		<i class="fa-solid fa-triangle-exclamation text-amber-600 dark:text-amber-400 mt-1" aria-hidden="true" />
		<div class="min-w-0 grow">
			<p class="font-semibold">This store has taken a payment and cannot send a receipt.</p>
			<p class="text-sm text-default">
				No email provider is configured, so receipts, refund confirmations and payment-failure
				notices are queued rather than sent. Nothing is lost: the queue is drained once a
				provider is saved, and anything whose wording has gone stale is discarded rather than
				sent late.
			</p>
		</div>
		<RoutedButton href="/Settings/Admin/Settings/Email" size="xs" color="alternative">
			Configure email
		</RoutedButton>
	</div>
{/if}

<!--
	The figures lead: they are what an operator opens this page to read, and every panel below
	them starts folded so the page opens on one screen. Outside the readiness block rather than
	inside it: the two loads fail for unrelated reasons, and a `setup-status` that times out must
	not take the figures down with it, nor the reverse.
-->
<div class="mb-4 space-y-4">
	<!--
		Two questions, in the order an operator asks them: what is the store doing, and who has an
		account on it. It was only ever the second — four figures about users and audit rows on the
		overview of a shop — so "is anybody buying anything" was a click away on Operations while
		this page counted logins.
	-->
	<Panel title="At a glance" faIcon="gauge" borderless>
		<p class="card-label mb-2"><i class="fa-solid fa-store text-[11px]" />The store</p>

		<!--
			Money first, and **one figure per currency**: every monetary metric arrives as a list
			keyed by currency and stays one all the way to the screen, because adding ¥1,000 to
			€1,000 produces a number that is not money in any currency and that nothing downstream
			could catch. `CurrencyFigure` is the shared rendering of that rule.
		-->
		<div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
			<CurrencyFigure
				label="Monthly recurring revenue"
				faIcon="arrow-trend-up"
				amounts={metrics?.mrr ?? []}
				emptyText={metricsLoading ? 'Reading…' : 'No active subscriptions yet.'}
			/>
			<CurrencyFigure
				label="Revenue, last 30 days"
				faIcon="sack-dollar"
				amounts={metrics?.revenueLast30Days ?? []}
				emptyText={metricsLoading ? 'Reading…' : 'Nothing taken in the last 30 days.'}
			/>
		</div>

		<div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
			<StatTile
				faIcon="repeat"
				tone="green"
				label="Active subscriptions"
				loading={metricsLoading}
				value={metrics?.activeSubscriptions ?? 0}
			/>
			<StatTile
				faIcon="user-check"
				tone="sky"
				label="Paying customers"
				loading={metricsLoading}
				value={metrics?.payingCustomers ?? 0}
			/>
			<StatTile
				faIcon="hourglass-half"
				tone="purple"
				label="Trialing"
				loading={metricsLoading}
				value={metrics?.trialingSubscriptions ?? 0}
			/>
			<!--
				Past due is the one figure here that is a job rather than a fact, so it says what it
				means in **words** as well as by colour: these customers still hold what they bought
				and the money has not arrived.
			-->
			<StatTile
				faIcon="triangle-exclamation"
				tone={(metrics?.pastDueSubscriptions ?? 0) > 0 ? 'red' : 'blue'}
				label="Past due"
				loading={metricsLoading}
				value={metrics?.pastDueSubscriptions ?? 0}
				detail={(metrics?.pastDueSubscriptions ?? 0) > 0 ? 'Payment failed; still entitled' : ''}
			/>
			<StatTile
				faIcon="arrow-trend-down"
				tone="yellow"
				label="Churn, last 30 days"
				loading={metricsLoading}
				value={metrics === null ? '—' : fnPercent(metrics.churnRate)}
				detail={metrics === null ? '' : `${metrics.canceledLast30Days} canceled`}
			/>
			<StatTile
				faIcon="credit-card"
				tone="red"
				label="Failed payments"
				loading={metricsLoading}
				value={(metrics?.failedPayments ?? []).reduce((sum, entry) => sum + entry.count, 0)}
				detail="Last 30 days"
			/>
			<StatTile
				faIcon="rotate-left"
				tone="yellow"
				label="Refund rate"
				loading={metricsLoading}
				value={metrics === null || metrics.refundRate.length === 0
					? '—'
					: fnPercent(Math.max(...metrics.refundRate.map(entry => entry.rate)))}
				detail={metrics !== null && metrics.refundRate.length > 1 ? 'Highest of any currency' : ''}
			/>
			<StatTile
				faIcon="money-bill-transfer"
				tone="purple"
				label="Chargebacks"
				loading={metricsLoading}
				value={(metrics?.chargebacksLast30Days ?? []).length}
				detail="Last 30 days"
			/>
		</div>

		{#if metricsError.length > 0}
			<div class="mt-4 p-4 bg-red-50 dark:bg-red-900/20 rounded-2xl text-red-700 dark:text-red-300">
				<i class="fa-solid fa-exclamation-triangle me-2"></i>
				The store figures could not be read. {metricsError}
				<Button size="xs" color="red" class="ms-4" on:click={fnLoadMetrics}>Retry</Button>
			</div>
		{/if}

		<p class="card-label mb-2 mt-6"><i class="fa-solid fa-users text-[11px]" />Accounts</p>
		<div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
			<StatTile faIcon="users" tone="blue" label="People" loading={countsLoading} value={stats.totalUsers} />
			<StatTile
				faIcon="shield"
				tone="yellow"
				label="Administrators"
				loading={countsLoading}
				value={stats.adminUsers}
			/>
			<StatTile
				faIcon="right-to-bracket"
				tone="green"
				label="Sign-ins (24h)"
				loading={countsLoading}
				value={stats.recentLogins}
				detail="From the latest page of the audit log"
			/>
			<StatTile
				faIcon="clipboard-list"
				tone="purple"
				label="Audit entries"
				loading={countsLoading}
				value={stats.auditEntries}
			/>
		</div>
	</Panel>

	{#if countsError.length > 0}
		<div class="p-4 bg-red-50 dark:bg-red-900/20 rounded-2xl text-red-700 dark:text-red-300">
			<i class="fa-solid fa-exclamation-triangle me-2"></i>
			{countsError}
			<Button size="xs" color="red" class="ms-4" on:click={fnLoadCounts}>
				Retry
			</Button>
		</div>
	{/if}
</div>

{#if loading}
	<EmptyState variant="loading" title="Checking the store…" />
{:else if loadError.length > 0}
	<EmptyState variant="error" title="The setup status could not be read." description={loadError}>
		<Button color="alternative" size="sm" on:click={fnLoad}>Try again</Button>
	</EmptyState>
{:else if readiness}
	<div class="space-y-4">
		<div class="widget bg-depth flex flex-wrap items-center gap-4">
			<div
				class="w-12 h-12 rounded-2xl flex items-center justify-center text-white shrink-0 {readiness.isOpen
					? 'bg-green-600'
					: 'bg-amber-500'}"
			>
				<i class="fa-solid fa-{readiness.isOpen ? 'check' : 'triangle-exclamation'} text-xl" />
			</div>
			<div class="min-w-0 grow">
				<!-- The state is stated in words, never in colour alone. -->
				<p class="text-lg font-semibold">
					{readiness.isOpen ? 'The store is open.' : 'The store is not open yet.'}
				</p>
				<p class="text-sm text-default">
					{readiness.isOpen
						? 'Every condition below is met, so checkout is available to customers.'
						: `${readiness.unmet.length} condition${readiness.unmet.length === 1 ? '' : 's'} still to meet. Until then the storefront says the store is not open rather than offering a Buy button that fails.`}
				</p>
			</div>
			<Button color="alternative" size="sm" on:click={fnRecheck}>
				<i class="fa-solid fa-rotate mr-2" />Re-check
			</Button>
		</div>

		<!--
			Health, alongside the gate rather than inside it. Nothing here blocks checkout — which is
			exactly why it needs saying: §6.2's inline reconcile keeps purchases completing with
			every worker stopped, so an operator watching the storefront sees a healthy store while
			renewals, entitlement delivery, email and reconciliation quietly do nothing.
		-->
		{#if readiness.health}
			{@const health = readiness.health}
			<Panel title="Behind the store" faIcon="heart-pulse" isHidden={true}>
				<svelte:fragment slot="titleRight">
					<RoutedButton href="/Settings/Admin/Operations" size="xs" color="alternative">
						Open operations
					</RoutedButton>
				</svelte:fragment>

				<div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
					<div class="widget bg-depth">
						<p class="card-label">Workers</p>
						<p class="text-sm">
							{#if !health.workersReporting}
								No worker has reported yet. Nothing behind the store is running.
							{:else if health.anyWorkerStale}
								{health.workers.filter(worker => worker.isStale).length} of {health.workers.length}
								stopped reporting.
							{:else}
								All {health.workers.length} reporting.
							{/if}
						</p>
					</div>
					<div class="widget bg-depth">
						<p class="card-label">Email queue</p>
						<p class="text-sm">
							{health.email.pending} pending, {health.email.failed} failed.
							{#if !health.email.configured}
								No provider configured, so nothing is being sent.
							{/if}
						</p>
					</div>
					<div class="widget bg-depth">
						<p class="card-label">Provider events</p>
						<p class="text-sm">
							{health.events.failed} failed.
							{#if health.events.oldestUnprocessedAt}
								Oldest unprocessed <RelativeDate date={health.events.oldestUnprocessedAt} />.
							{:else}
								Nothing waiting.
							{/if}
						</p>
					</div>
					<div class="widget bg-depth">
						<p class="card-label">Entitlement delivery</p>
						<p class="text-sm">
							{health.deliveries.pending} in flight, {health.deliveries.dead} dead.
							{#if health.deliveries.dead > 0}
								Each dead row is an application diverged until its resync lands.
							{/if}
						</p>
					</div>
				</div>

				{#each health.workers.filter(worker => worker.lastError !== null) as worker (worker.workerName + worker.instanceID)}
					<p class="text-sm text-amber-700 dark:text-amber-400 mt-3">
						{worker.workerName} last failed with: {worker.lastError}
					</p>
				{/each}
			</Panel>
		{/if}

		<!-- Folded once the store is open; a closed store opens on what is blocking it. -->
		<Panel title="Readiness conditions" faIcon="clipboard-check" isHidden={readiness.isOpen}>
			<ul class="space-y-3">
				{#each CHECKLIST as entry (entry.condition)}
					{@const unmet = unmetByCondition.get(entry.condition)}
					<li class="flex flex-wrap items-start gap-3">
						<i
							class="fa-solid fa-{unmet ? 'circle-xmark' : 'circle-check'} mt-1 {unmet
								? 'text-amber-500'
								: 'text-green-600'}"
							aria-hidden="true"
						/>
						<div class="min-w-0 grow">
							<p class="font-medium">
								{entry.title}
								<span class="ml-2 text-xs uppercase tracking-wide {unmet ? 'text-amber-600 dark:text-amber-400' : 'text-green-700 dark:text-green-400'}">
									{unmet ? 'Not met' : 'Met'}
								</span>
							</p>
							<p class="text-sm text-default">{entry.detail}</p>
							{#if unmet}
								<p class="text-sm text-amber-700 dark:text-amber-400 mt-1">{unmet.message}</p>
							{/if}
						</div>

						{#if unmet && entry.href}
							<RoutedButton href={entry.href} size="xs" color="alternative">
								{entry.action}
							</RoutedButton>
						{:else if unmet}
							<span class="text-xs text-default">Arrives with the catalog</span>
						{/if}
					</li>
				{/each}

				{#each unrecognised as unmet (unmet.condition)}
					<li class="flex flex-wrap items-start gap-3">
						<i class="fa-solid fa-circle-xmark mt-1 text-amber-500" aria-hidden="true" />
						<div class="min-w-0 grow">
							<p class="font-medium">{unmet.condition} <span class="ml-2 text-xs uppercase tracking-wide text-amber-600 dark:text-amber-400">Not met</span></p>
							<p class="text-sm text-amber-700 dark:text-amber-400">{unmet.message}</p>
						</div>
					</li>
				{/each}
			</ul>
		</Panel>
	</div>
{/if}

<!-- The shortcuts and the build facts, folded like everything below the figures. -->
<div class="mt-4 space-y-6">

	<!-- Quick Actions -->
	<Panel title="Quick Actions" faIcon="bolt" isHidden={true}>
		<div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
			<button on:click={navigateToUsers} class="widget bg-depth bg-depth-hover p-4 text-left">
				<div class="flex items-center gap-3 mb-2">
					<i class="fa-solid fa-user-gear text-primary-600 dark:text-primary-400"></i>
					<span class="font-medium text-gray-900 dark:text-white">Manage Users</span>
				</div>
				<p class="text-sm text-default">
					View, search, and manage user accounts
				</p>
			</button>

			<button on:click={navigateToLogs} class="widget bg-depth bg-depth-hover p-4 text-left">
				<div class="flex items-center gap-3 mb-2">
					<i class="fa-solid fa-scroll text-primary-600 dark:text-primary-400"></i>
					<span class="font-medium text-gray-900 dark:text-white">Audit Logs</span>
				</div>
				<p class="text-sm text-default">
					Review system activity and security events
				</p>
			</button>

			<button on:click={fnLoadCounts} class="widget bg-depth bg-depth-hover p-4 text-left">
				<div class="flex items-center gap-3 mb-2">
					<i class="fa-solid fa-refresh text-primary-600 dark:text-primary-400"></i>
					<span class="font-medium text-gray-900 dark:text-white">Refresh Stats</span>
				</div>
				<p class="text-sm text-default">
					Reload all administration statistics
				</p>
			</button>
		</div>
	</Panel>

	<!-- System Information -->
	<Panel title="System Information" faIcon="circle-info" isHidden={true}>
		<div class="grid grid-cols-1 md:grid-cols-2 gap-4">
			<div class="space-y-3">
				<div class="flex justify-between py-2 border-b border-gray-200 dark:border-gray-700">
					<span class="text-default">Application</span>
					<span class="text-gray-900 dark:text-white">{$configurationStore?.name || 'Abstract Store'}</span>
				</div>
				<div class="flex justify-between py-2 border-b border-gray-200 dark:border-gray-700">
					<span class="text-default">Environment</span>
					<Badge color="yellow">
						N/A
					</Badge>
				</div>
				<div class="flex justify-between py-2">
					<span class="text-default">Version</span>
					<span class="text-gray-900 dark:text-white">{$configurationStore?.version || '1.0.0'}</span>
				</div>
			</div>

			<div class="space-y-3">
				<div class="flex justify-between py-2 border-b border-gray-200 dark:border-gray-700">
					<span class="text-default">Auth Provider</span>
					<span class="text-gray-900 dark:text-white">AbstractID</span>
				</div>
				<div class="flex justify-between py-2 border-b border-gray-200 dark:border-gray-700">
					<span class="text-default">Database</span>
					<span class="text-gray-900 dark:text-white">PostgreSQL</span>
				</div>
				<div class="flex justify-between py-2">
					<span class="text-default">Framework</span>
					<span class="text-gray-900 dark:text-white">Svelte 4 + Express</span>
				</div>
			</div>
		</div>
	</Panel>
</div>
